/* Onixx header search — loaded (deferred) by sections/header.liquid */
/* ============================================================
   ONIXX header search — Dawn edition
   - <onixx-search> custom element (inline bar + popup instance)
   - rotating product names in the empty field
   - before typing: recent / popular searches + recommended products
   - while typing: Shopify predictive search (/search/suggest.json)
   - search icon -> full-screen popup ([data-search-popup-open])
   ============================================================ */

(() => {
  if (customElements.get('onixx-search')) return;

  const RECENT_KEY = 'onixx-recent-searches';
  const RECENT_MAX = 5;
  const ROTATE_MS = 2800;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const escapeHtml = (str = '') =>
    String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // The typed letters are wrapped in <mark> (shown in the accent colour).
  // Matches at the start of words win ("half rack Annex"); otherwise the first match anywhere.
  const highlight = (text, query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return escapeHtml(text);
    const lower = text.toLowerCase();
    const hits = [];
    for (let i = lower.indexOf(q); i > -1; i = lower.indexOf(q, i + q.length)) {
      if (i === 0 || /[\s\-\/(&]/.test(text[i - 1])) hits.push(i);
    }
    if (!hits.length) {
      const first = lower.indexOf(q);
      if (first < 0) return escapeHtml(text);
      hits.push(first);
    }
    let out = '';
    let pos = 0;
    hits.forEach((i) => {
      out += escapeHtml(text.slice(pos, i)) + `<mark>${escapeHtml(text.slice(i, i + q.length))}</mark>`;
      pos = i + q.length;
    });
    return out + escapeHtml(text.slice(pos));
  };

  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const saveRecent = (term) => {
    const t = (term || '').trim();
    if (!t) return;
    try {
      const list = readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase());
      list.unshift(t);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* storage blocked — recent searches just won't persist */
    }
  };

  const clearRecent = () => {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (e) {}
  };

  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const ARROW_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  class OnixxSearch extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.cache = new Map();
      this.activeIndex = -1;
      this.query = '';
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-panel]');
      this.clearBtn = this.querySelector('[data-clear]');
      this.rotator = this.querySelector('[data-rotator]');
      this.roll = this.querySelector('[data-roll]');
      this.prefixEl = this.querySelector('[data-prefix]');
      this.suffixEl = this.querySelector('[data-suffix]');

      const d = this.dataset;
      this.isPopup = d.variant === 'popup';
      this.suggestUrl = d.suggestUrl;
      this.searchUrl = d.searchUrl;
      this.predictive = d.predictive !== 'false';
      this.limit = parseInt(d.limit, 10) || 5;
      this.showPrice = d.showPrice !== 'false';
      this.terms = (d.fallbackTerms || '').split(',').map((t) => t.trim()).filter(Boolean);
      this.recommended = this.readData().products || [];

      try {
        this.moneyFormat = new Intl.NumberFormat(d.locale || undefined, { style: 'currency', currency: d.currency || 'USD' });
      } catch (e) {
        this.moneyFormat = null;
      }

      this.bind();
      this.syncValueState();
      if (d.random !== 'false') this.startRotation();
    }

    disconnectedCallback() {
      clearInterval(this.rotateTimer);
      document.removeEventListener('pointerdown', this.onOutside);
    }

    readData() {
      const el = document.getElementById(this.dataset.source);
      if (!el) return {};
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return {};
      }
    }

    /* ---------- events ---------- */

    bind() {
      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('click', () => this.open());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));

      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });

      this.form.addEventListener('submit', (e) => {
        let q = this.input.value.trim();
        // empty field while a product name is rolling -> search that name
        if (!q && this.currentName) {
          q = this.currentName;
          this.input.value = q;
        }
        if (!q) {
          e.preventDefault();
          return;
        }
        saveRecent(q);
      });

      this.panel.addEventListener('click', (e) => {
        if (e.target.closest('[data-clear-recent]')) {
          e.preventDefault();
          clearRecent();
          this.render();
          this.input.focus();
          return;
        }
        const link = e.target.closest('a.onixx-item');
        if (!link) return;
        saveRecent(link.dataset.term || this.input.value);
      });

      // stop clicks inside the panel from stealing focus and closing it
      this.panel.addEventListener('mousedown', (e) => {
        if (!e.target.closest('a')) e.preventDefault();
      });

      if (!this.isPopup) {
        this.onOutside = (e) => {
          if (this.isOpen && !this.contains(e.target)) this.close();
        };
        document.addEventListener('pointerdown', this.onOutside);
        this.addEventListener('focusout', (e) => {
          if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
        });
      }
    }

    onInput() {
      this.syncValueState();
      this.open();
      const q = this.input.value.trim();
      this.query = q;
      clearTimeout(this.debounce);
      if (!q) {
        this.abort();
        this.render();
        return;
      }
      this.debounce = setTimeout(() => this.fetchResults(q), 180);
    }

    onKeydown(e) {
      const items = [...this.panel.querySelectorAll('.onixx-item')];
      switch (e.key) {
        case 'ArrowDown':
          if (!this.isOpen) this.open();
          if (!items.length) return;
          e.preventDefault();
          this.setActive(items, this.activeIndex + 1 >= items.length ? 0 : this.activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!items.length) return;
          e.preventDefault();
          this.setActive(items, this.activeIndex - 1 < 0 ? items.length - 1 : this.activeIndex - 1);
          break;
        case 'Enter':
          if (this.activeIndex > -1 && items[this.activeIndex]) {
            e.preventDefault();
            items[this.activeIndex].click();
          }
          break;
        case 'Escape':
          if (this.isPopup) {
            closePopup();
          } else if (this.isOpen) {
            e.preventDefault();
            this.close();
          } else {
            this.input.blur();
          }
          break;
      }
    }

    setActive(items, index) {
      items.forEach((el) => el.classList.remove('is-active'));
      this.activeIndex = index;
      const el = items[index];
      if (!el) return;
      el.classList.add('is-active');
      el.scrollIntoView({ block: 'nearest' });
    }

    syncValueState() {
      const has = this.input.value.length > 0;
      this.classList.toggle('has-value', has);
      this.clearBtn.hidden = !has;
    }

    /* ---------- open / close ---------- */

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.query = this.input.value.trim();
      if (this.query) this.fetchResults(this.query);
      else this.render();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      this.activeIndex = -1;
      this.abort();
    }

    /* ---------- data ---------- */

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.panel.classList.remove('is-loading');
    }

    async fetchResults(q) {
      if (!this.predictive) {
        this.render({ queries: [], products: [] });
        return;
      }
      const key = q.toLowerCase();
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      this.abort();
      this.controller = new AbortController();
      this.panel.classList.add('is-loading');

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,query',
        'resources[limit]': String(this.limit),
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      });

      try {
        const res = await fetch(`${this.suggestUrl}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const json = await res.json();
        const r = (json.resources && json.resources.results) || {};
        const data = {
          queries: (r.queries || []).map((x) => x.text),
          products: (r.products || []).map((p) => ({
            title: p.title,
            url: p.url,
            image: (p.featured_image && p.featured_image.url) || p.image || null,
            price: this.formatPrice(p.price),
          })),
        };
        this.cache.set(key, data);
        this.panel.classList.remove('is-loading');
        if (this.input.value.trim() === q) this.render(data);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.panel.classList.remove('is-loading');
        this.render({ queries: [], products: [], failed: true });
      }
    }

    formatPrice(raw) {
      if (raw == null || raw === '') return '';
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return String(raw);
      return this.moneyFormat ? this.moneyFormat.format(n) : n.toFixed(2);
    }

    thumbUrl(src) {
      if (!src) return '';
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '160');
        return url.toString();
      } catch (e) {
        return src;
      }
    }

    searchHref(term) {
      const p = new URLSearchParams({ q: term, 'options[prefix]': 'last' });
      return `${this.searchUrl}?${p}`;
    }

    /* ---------- rendering ---------- */

    render(results) {
      if (!this.isOpen) return;
      const q = this.input.value.trim();
      this.activeIndex = -1;

      const html = q ? this.resultsHtml(q, results || { queries: [], products: [] }) : this.emptyHtml();
      if (!html) {
        this.panel.hidden = true;
        this.panel.innerHTML = '';
        return;
      }
      this.panel.innerHTML = html;
      this.panel.hidden = false;
      this.panel.scrollTop = 0;
    }

    termList(terms, query) {
      return `<ul>${terms
        .map(
          (t) =>
            `<li><a class="onixx-item onixx-term" href="${escapeHtml(this.searchHref(t))}" data-term="${escapeHtml(t)}"><span class="onixx-term-text">${
              query ? highlight(t, query) : escapeHtml(t)
            }</span></a></li>`
        )
        .join('')}</ul>`;
    }

    productList(products) {
      return `<ul>${products
        .map((p) => {
          const img = p.image
            ? `<img src="${escapeHtml(this.thumbUrl(p.image))}" alt="" width="52" height="52" loading="lazy">`
            : '';
          const price = this.showPrice && p.price ? `<span class="onixx-product-price">${escapeHtml(p.price)}</span>` : '';
          return `<li><a class="onixx-item" href="${escapeHtml(p.url)}"><span class="onixx-thumb">${img}</span><span class="onixx-product-text"><span class="onixx-product-title">${escapeHtml(
            p.title
          )}</span>${price}</span></a></li>`;
        })
        .join('')}</ul>`;
    }

    grid(cols) {
      const filled = cols.filter(Boolean);
      if (!filled.length) return '';
      return `<div class="onixx-grid${filled.length === 1 ? ' onixx-grid--single' : ''}">${filled.join('')}</div>`;
    }

    emptyHtml() {
      const recent = readRecent();
      let left = '';
      if (recent.length) {
        left = `<div class="onixx-col"><p class="onixx-heading">Recent searches <button type="button" class="onixx-link-btn" data-clear-recent>Clear</button></p>${this.termList(
          recent
        )}</div>`;
      } else if (this.terms.length) {
        left = `<div class="onixx-col"><p class="onixx-heading">Popular searches</p>${this.termList(this.terms.slice(0, this.limit))}</div>`;
      }

      const recs = this.recommended.slice(0, this.limit);
      const right = recs.length
        ? `<div class="onixx-col"><p class="onixx-heading">Recommended for you</p>${this.productList(recs)}</div>`
        : '';

      return this.grid([left, right]);
    }

    resultsHtml(q, data) {
      if (data.failed) {
        return `<p class="onixx-note">Something went wrong. Press enter to see all results.</p>${this.footerHtml(q)}`;
      }

      const queries = data.queries.length ? data.queries.slice(0, this.limit) : [q];
      const left = `<div class="onixx-col"><p class="onixx-heading">Suggestions</p>${this.termList(queries, q)}</div>`;

      let right;
      if (data.products.length) {
        right = `<div class="onixx-col"><p class="onixx-heading">Products</p>${this.productList(data.products.slice(0, this.limit))}</div>`;
      } else {
        const recs = this.recommended.slice(0, Math.min(3, this.limit));
        right = `<div class="onixx-col"><p class="onixx-heading">Products</p><p class="onixx-note" style="padding:4px 0 12px">No products match “${escapeHtml(
          q
        )}”.</p>${recs.length ? `<p class="onixx-heading">You might like</p>${this.productList(recs)}` : ''}</div>`;
      }

      return this.grid([left, right]) + this.footerHtml(q);
    }

    footerHtml(q) {
      return `<a class="onixx-item onixx-footer" href="${escapeHtml(this.searchHref(q))}" data-term="${escapeHtml(q)}"><span>Search for “${escapeHtml(
        q
      )}”</span>${ARROW_SVG}</a>`;
    }

    /* ---------- rotating placeholder ---------- */

    startRotation() {
      const names = this.recommended.map((p) => p.title).filter(Boolean);
      this.names = shuffle(names.length ? names : this.terms);
      if (!this.names.length || !this.rotator) return;

      this.prefixEl.textContent = '';
      this.suffixEl.textContent = '';
      this.nameIndex = 0;
      this.classList.add('is-rotating');
      this.showName(this.names[0], false);

      this.rotateTimer = setInterval(() => {
        // skip while hidden (inline bar on small screens, closed popup) or while typing
        if (document.hidden || !this.offsetParent || this.input.value) return;
        this.nameIndex = (this.nameIndex + 1) % this.names.length;
        this.showName(this.names[this.nameIndex], !reduceMotion.matches);
      }, ROTATE_MS);

      if (this.isPopup) document.addEventListener('onixx:popup-open', () => this.showName(this.currentName, false));
    }

    updateCap() {
      const cap = this.rotator.clientWidth - this.prefixEl.offsetWidth - this.suffixEl.offsetWidth;
      if (cap > 0) this.roll.style.setProperty('--cap', `${cap}px`);
    }

    showName(name, animate) {
      this.currentName = name;
      const current = this.roll.querySelector('.onixx-roll-item:not(.onixx-roll-item--in)');
      this.updateCap();

      if (!animate || !current || !current.animate) {
        this.roll.innerHTML = '';
        const span = document.createElement('span');
        span.className = 'onixx-roll-item';
        span.textContent = name;
        this.roll.appendChild(span);
        return;
      }

      // leftover from an interrupted animation
      this.roll.querySelectorAll('.onixx-roll-item--in').forEach((el) => el.remove());

      const next = document.createElement('span');
      next.className = 'onixx-roll-item onixx-roll-item--in';
      next.textContent = name;
      this.roll.appendChild(next);

      const timing = { duration: 380, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' };
      current.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-100%)', opacity: 0 }], timing);
      next.animate([{ transform: 'translateY(100%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], timing).onfinish = () => {
        current.remove();
        next.classList.remove('onixx-roll-item--in');
        next.getAnimations().forEach((a) => a.cancel());
      };
    }
  }

  customElements.define('onixx-search', OnixxSearch);

  /* ============================================================
     Popup controller
     ============================================================ */

  let lastFocus = null;

  const getPopup = () => document.querySelector('[data-search-popup]');

  function openPopup(opener) {
    const popup = getPopup();
    if (!popup || !popup.hidden) return;

    // close Dawn's menu drawer if the popup was opened from inside it
    const drawer = opener && opener.closest('details[open]');
    if (drawer) {
      drawer.removeAttribute('open');
      document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet');
    }

    lastFocus = opener || document.activeElement;
    popup.hidden = false;
    document.documentElement.classList.add('onixx-search-open');
    document.dispatchEvent(new CustomEvent('onixx:popup-open'));

    const search = popup.querySelector('onixx-search');
    if (search) {
      search.open();
      // focus after display so iOS opens the keyboard
      requestAnimationFrame(() => search.input.focus({ preventScroll: true }));
    }
  }

  function closePopup() {
    const popup = getPopup();
    if (!popup || popup.hidden) return;
    const search = popup.querySelector('onixx-search');
    if (search) search.close();
    popup.hidden = true;
    document.documentElement.classList.remove('onixx-search-open');
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-search-popup-open]');
    if (opener) {
      e.preventDefault();
      openPopup(opener);
      return;
    }
    if (e.target.closest('[data-search-popup-close]')) {
      e.preventDefault();
      closePopup();
    }
  });

  // Escape anywhere + keep Tab inside the open popup
  document.addEventListener('keydown', (e) => {
    const popup = getPopup();
    if (!popup || popup.hidden) return;

    if (e.key === 'Escape') {
      closePopup();
      return;
    }
    if (e.key !== 'Tab') return;

    const focusables = [...popup.querySelectorAll('a[href], button:not([disabled]):not([hidden]), input:not([type="hidden"])')].filter(
      (el) => el.offsetParent !== null
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // theme editor: section re-render leaves the page scroll-locked otherwise
  document.addEventListener('shopify:section:unload', () => document.documentElement.classList.remove('onixx-search-open'));

  window.OnixxSearchPopup = { open: openPopup, close: closePopup };
})();